<template>
  <div class="container p-0">

    <!-- Small screen button nav -->
    <div
      v-if="isEditingCampaign"
      class="col-12 d-block d-md-none">

      <button-nav
        :buttons="avalableActions"
        :tooltips="getTooltips"
        :disable="{'activate': getMpeIssues}"
        @prevStep="stepBack"
        @activate="toggleCampaign('active')"
        @stopCampaign="toggleCampaign('paused')" />

      <hr class="mt-0 p-0" />
    </div>

    <!-- Campaign's label + top action buttons -->
    <div class="row m-0 m-0">
      <p class="col pl-0 pr-0 m-0">
        <span class="float-left centering">
          <h4 class="break-text campaign-title">
            {{ campaignLabel }}

          </h4>
        </span>

        <button-nav
          v-if="isEditingCampaign"
          class="float-right d-none d-sm-none d-md-flex"
          :disable="{'activate': getMpeIssues}"
          :buttons="avalableActions"
          :tooltips="getTooltips"
          @prevStep="stepBack"
          @activate="toggleCampaign('active')"
          @stopCampaign="toggleCampaign('paused')" />

      </p>
    </div>

    <campaign-grid
      :activeDate="getViewedDate"
      :listItems="getFilteredMpes"
      :removing="removingEvents"
      :defaultStyle="style"
      :today="getToday"
      :minDate="getEditedCampaignStartDate"
      :maxDate="getEditedCampaignEndDate"
      :dateRangePickerPresets="getDateRangePresets"
      allowRangePicker
      useCalendarNavigation
      dataSource="mpe"
      variant="detailed"
      @changeDate="setFilteredDate"
      @setDisplayStyle="setDisplayStyle"
      @editMpe="editMpe"
      @removeMpe="removeMpe"
      @setSchedule="setSchedule"
      @duplicate="duplicate" >

      <div
        slot="grid-date-prefix"
        class="break-text date-range-picker-prefix">
        
        {{ campaignLabel }}
      </div>
      
      <!-- Extra MPE filtering -->
      <div
        slot="extraFilters"
        class="mpe-filters"
        data-v-step="campaignsView.view.calendar.viewManagement">

        <input-select
          small
          transparent
          variant="standardInput"
          :inputLabel="this.$t('Campaigns.filterType')"
          :inputPlaceholder="this.$t('Button.select')"
          :selectOptions="mpeTypeFilter"
          :defaultValue="getTypeFilter"
          @fieldUpdate="setTypeFilter"
          inputName="mpeTypeFilter" />

        <input-select
          small
          transparent
          variant="standardInput"
          :inputLabel="this.$t('Campaigns.filterChannel')"
          :inputPlaceholder="this.$t('Button.select')"
          :selectOptions="getChannelOptions"
          :defaultValue="getChannelFilter"
          @fieldUpdate="setChannelFilter"
          inputName="getChannelOptions" />

        <input-button
          v-if="areFiltersActive"
          class="filter-clear-button"
          variant="standatdInput"
          btnStyle="grayed-out-style"
          :inputPlaceholder="this.$t('Campaigns.filterClear')"
          @click="clearFilters()" />

      </div>
    </campaign-grid>

    <editor-wrapper
      v-if="editing"
      @discardChanges="discardChanges"
      @saveMpe="saveMpe"
      :mpeId="editedMpeId" />

    <div class="col-12 bottom-buttons p-0">
      <button-nav
        v-if="isEditingCampaign"
        :buttons="avalableActions"
        :tooltips="getTooltips"
        :disable="{'activate': getMpeIssues}"
        @prevStep="stepBack"
        @activate="toggleCampaign('active')"
        @stopCampaign="toggleCampaign('paused')" />

    </div>
  </div>
</template>

<script>
import InputSelect from '@/components/standard/input/InputSelect'
import InputButton from '@/components/standard/input/InputButton'
import ButtonNav from '@/components/standard/action/ButtonNav'
import CampaignGrid from '@/components/standard/display/CampaignGrid'
import EditorWrapper from '@/components/service/campaignManager/standaloneMpe/EditorWrapper'

import apiConnector from '@/mixins/apiConnector.js'
import campaignEditor from '@/mixins/pagination/providers/campaignEditor'

import fieldBuilder from '@/assets/modules/apollo/fieldBuilder'
import nManager from '@/assets/modules/notifications/manager'
import resolver from '@/assets/modules/error/resolver'
import objectHelpers from '@/assets/modules/objectHelpers'
import makeDates from '@/assets/modules/displayGrids/makeDates'
import routingHelper from '@/assets/modules/routing/routingHelper'
import paramExtractor from '@/assets/modules/channels/paramExtractor'

import { checkContentLength } from '@/assets/modules/constraints/payload'

import drpg from '@/assets/modules/inputs/dateRangePresetGenerator'

import moment from 'moment'

const CONTENT_CUT = 'Warning.mpe.payload.cut'

const baseFilters = () => ({
  channel: null,
  type: null,
  date: {}
})

export default {
  data () {
    let style = this.$route.params.view || 'grid'

    return {
      editing: null,

      mpeTypeFilter: [
        {
          label: 'Campaign.mpeTypeFilter.post',
          val: 'post'
        },
        {
          label: 'Campaign.mpeTypeFilter.product',
          val: 'product'
        },
        {
          label: 'Campaign.mpeTypeFilter.importedProduct',
          val: 'importedProduct'
        }
      ],

      filterFields: baseFilters(),
      // Default previus ranges for grid and calendar
      previousRanges: {
        /* grid: 
        calendar: */
      },

      style,
      
      editingId: this.$route.params.id,
      removingEvents: {}
    }
  },

  methods: {
    /**
     * Duplicates the given MPE
     * @param {Object} e MPE to duplicate
     */
    duplicate (e) {
      let { id, advertisable, advertisableType } = e
      if (this.$route.name !== 'editQuickPostGroup') {
        this.duplicateMpe(id, advertisableType, true, { campaign: this.campaignId })
      } else {
        this.duplicateMpe(id, advertisableType)
      }
    },

    /**
     * Set's event's status as resolving
     * @param {String} id
     */
    markEventAsRemoving (id) {
      this.$set(this.removingEvents, id, true)
    },

    /**
     * Set's event's status as resolved
     * @param {String} id
     */
    markEventAsRemoved (id) {
      this.$delete(this.removingEvents, id)
    },

    /**
     * Gives view's group
     * @param {String} style
     * @returns {String}
     */
    getViewGroup (style) {
      return resolver.isCalendarView(style) ? 'calendar' : 'grid'
    },

    /**
     * Gives group's base date range. Looks at previus set filters, or generates 
     * a new one
     * @param {String} viewGroup
     * @returns {Object}
     */
    getGroupBaseDateRange (viewGroup) {
      let ecd = this.getEditedCampaignData
      return this.previousRanges[viewGroup] || this.getInitialDateRange(ecd.startsAt, ecd.expiresAt, this.style)
    },

    /**
     * Resets filters back to there original state
     */
    clearFilters () {
      this.$set(this, 'filterFields', baseFilters())
      this.$set(this, 'previousRanges', {})
    },

    /**
     * Updatef filter's field with the given value
     * @param {String} field
     * @param {String} value
     */
    setFilter (field, value) {
      if (!this.filterFields) this.$set(this, 'filterFields', baseFilters())
      this.$set(this.filterFields, field, value)
    },

    /**
     * Updates date filter
     * @param {Object} e Object of date params
     */
    setFilteredDate (e) {
      let { from, to, id } = e

      let date = {
        from,
        to,
        id
      }

      let viewGroup = this.getViewGroup(this.style)
      this.$set(this.previousRanges, viewGroup, date)
      this.setFilter('date', date)

      if (this.isCalendarView) {
        this.refetchCampaignParams()
      }
    },

    /**
     * Changes grid display style
     * @param {String} style New grid style
     */
    setDisplayStyle (style) {
      // If clicked on same display style just stop
      if (this.style === style) return

      // If previous or new view is a calendar view, refetch campaign
      if (resolver.isCalendarView(style) || resolver.isCalendarView(this.style)) this.refetchCampaignParams()
      this.style = style

      // Get date rangebase for given view
      let viewGroup = this.getViewGroup(this.style)
      let base = this.getGroupBaseDateRange(viewGroup)

      if (resolver.isCalendarView(style)) {
        // Tweak date based on calendar type
        let from = moment(base.from).startOf(this.getDataRangeFieldFrmStyle).toISOString()
        let id = from
        let to = moment(from).endOf(this.getDataRangeFieldFrmStyle).toISOString()

        if (style === 'agendaWeek') {
          let corrected = makeDates.correctMonthToWeekRange(from, to)
          from = corrected.from
          id = from
          to = corrected.to
        }

        this.setFilter('date', {
          from,
          id,
          to
        })
      } else {
        this.setFilter('date', base)
      }
    },
  
    /**
     * Discards changes made to the curent edited MPE
     */
    discardChanges (e) {
      this.closeEditor()
    },

    /**
     * Saves the edited MPE
     * @prop {Object} e Edited MPE object
     */
    saveMpe (e) {
      let gqlObject = {
        id: this.editing,
        advertisable: e.params.advertisable,
        publishAt: e.params.startsAt,
        removeAt: e.params.expiresAt,
        type: e.type
      }
      gqlObject[e.type] = e.payload

      let media = (Object.keys(e.payloadMedia || {})).map(m => { return { url: m } })

      gqlObject[e.type][resolver.getMediaFieldName(e.type)] = media

      if (e.clearId) {
        delete gqlObject.id
      }

      this.saveChanges(gqlObject, true)
    },

    /**
     * Closes MPE editor
     */
    closeEditor () {
      this.editing = null
    },

    /**
     * Sets a channel filter for MPE's
     * @param {StandardInputObject} e Selected option
     */
    setChannelFilter (e) {
      if (this.isCalendarView) {
        this.refetchCampaignParams()
      }

      let channel = (e.value === 'dft') ? null : e.value
      this.setFilter('channel', channel)
    },

    /**
     * Sets a MPE type filter for MPE's
     * @param {StandardInputObject} e Selected option
     */
    setTypeFilter (e) {
      if (this.isCalendarView) {
        this.refetchCampaignParams()
      }

      let type = (e.value === 'dft') ? null : e.value
      this.setFilter('type', type)
    },

    /**
     * Returns campaign date field
     * @param {Object} campaign Campaign we are interested in
     * @returns {Object} Campaign date object
     */
    getCampaignDataFields (campaign) {
      let rtr = {
        expiresAt: moment(campaign.expiresAt),
        label: campaign.label,
        startsAt: moment(campaign.startsAt)
      }

      rtr[campaign.type] = campaign.meta
      return rtr
    },

    /**
     * Goes to previus step. Will have to remove the campaign and push it's data
     * back to campaign creator
     */
    stepBack () {
      let params = {
        category: paramExtractor.extract(this.getEditedCampaignType).campaignType,
        id: this.campaignId
      }

      this.$router.push({ name: 'createCampaign', params })
    },

    /**
     * Toggles campaign's status
     * @param {String} status Campaign's new status
     */
    toggleCampaign (status) {
      let okCallback = (e) => {
        nManager.okNotification(this, 'Notification.ok.campaignStatusUpdated.label')
        if (status === 'active') {
          this.$router.push({ name: 'campaignPreview', params: {id: e.id} })
        } else {
          this.$apollo.queries.campaigns.refetch()
        }
      }

      let failCallback = (_, e) => {
        let message = 'Status kampanje se ni posodobil'
        message = resolver.resolve(e.message, message)
        nManager.nokNotification(this, message)
      }

      let co = {
        id: this.campaignId,
        status,
        type: this.getEditedCampaignType
      }

      let {mutationString, dataObject, name} = fieldBuilder
        .buildGqlObject(co, 'updateCampaign', 'mutation', [], {
          status: 'CampaignStatus',
          type: 'CampaignType'
        })

      this.sendMutation(
        mutationString,
        dataObject,
        okCallback,
        failCallback,
        name)
    },

    /**
     * This will refresh campaigns that are dealing with campaign editor
     */
    refetchCampaignParams () {
      this.$apollo.queries.campaigns.refresh()
      this.$apollo.queries.mediaPlanEvents.refresh()
    },

    /**
     * Saves changes made to campaign's event
     * @param {Object} e Edited mpe's object
     */
    saveChanges (e, fromMpeEditor = false) {
      let okCallback = (e) => {
        nManager.okNotification(this, 'Notification.ok.updatesSaved.label')

        // Grud and list views can refetch them right away. Calendar will refetch on
        // style change
        if (!this.isCalendarView || fromMpeEditor) {
          this.refetchCampaignParams()
        }

        this.closeEditor()
      }

      let failCallback = (_, e) => {
        let message = 'Spremembe se niso shranile'
        message = resolver.resolve(e.message, message)
        nManager.nokNotification(this, message)
      }
      
      let {mutationString, dataObject, name} = fieldBuilder
        .buildGqlObject(e, 'storeMediaPlanEvent', 'mutation', [], {
          type: 'MediaPlanEventType',
          status: 'CampaignStatus'
        })

      this.sendMutation(
        mutationString,
        dataObject,
        okCallback,
        failCallback,
        name)
    },

    /**
     * Tries to remove a MPE from it's channel.
     * @param {Object} e MPE to be removed
     * @param {Callbakk} afterCallback Callback to be called after the mutation resolves
     */
    removeMpeFromChannel (e, afterCallback = null) {
      let oE = e
      let okCallback = (e) => {
        this.markEventAsRemoved(oE.id)
        nManager.okNotification(this, 'Notification.ok.postDeleted.label')
        this.$apollo.queries.campaigns.refresh()

        this.refetchCampaignParams()
        if (afterCallback !== null) {
          ((afterCallback)(oE))
        }
      }

      let failCallback = (_, e) => {
        this.markEventAsRemoved(oE.id)
        let message = 'Objava ni bila izbrisana'
        message = resolver.resolve(e.message, message)
        nManager.nokNotification(this, message)

        if (afterCallback !== null) {
          ((afterCallback)(oE))
        }
      }

      let {mutationString, dataObject, name} = fieldBuilder
        .buildGqlObject(e, 'removePublishedMediaPlanEvent', 'mutation')

      this.sendMutation(
        mutationString,
        dataObject,
        okCallback,
        failCallback,
        name)
    },

    /**
     * Tries to remove a MPE from it's campaign
     * @param {Object} e MPE to be removed
     */
    removeMpeFromCampaign (e) {
      let okCallback = (e) => {
        this.markEventAsRemoved(e.id)
        nManager.okNotification(this, 'Notification.ok.postDeleted.label')
        this.refetchCampaignParams()
      }

      let failCallback = (_, e) => {
        this.markEventAsRemoved(e.id)
        let message = 'Objava ni bila izbrisana'
        message = resolver.resolve(e.message, message)
        nManager.nokNotification(this, message)
      }

      let {mutationString, dataObject, name} = fieldBuilder
        .buildGqlObject(e, 'deleteMediaPlanEvent', 'mutation')

      this.sendMutation(
        mutationString,
        dataObject,
        okCallback,
        failCallback,
        name)
    },

    /**
     * Removes a MPE from it's channel and from it's campaign.
     * @param {Object} e MPE to be removed
     */
    removeMpe (e) {
      this.markEventAsRemoving(e.id)

      // Check where that MPE can be removedfrom
      let places = resolver.whereToRemoveFrom(this.getMpeStatus(e.id))
      if (places.channel && places.campaign) {
        this.removeMpeFromChannel(e, this.removeMpeFromCampaign)
      } else if (!places.channel && places.campaign) {
        this.removeMpeFromCampaign(e)
      } else if (places.channel && !places.campaign) {
        this.removeMpeFromChannel(e, null)
      } else {
        this.removeMpeFromCampaign(e)
      }
    },

    /**
     * Sets campaign to be edited
     * @param {Object} e MPE to be edited
     */
    editMpe (e) {
      this.editing = e.id
    },

    /**
     * Updates schedule
     * @param {e} e Updated MPE object
     */
    setSchedule (e) {
      this.saveChanges(e)
    },

    /**
     * Extracts all unique mpe issues
     * @param {Array} mpes
     * @returns {Array}
     */
    extractMpeIssues (mpes = []) {
      return Array.from(mpes.reduce((acc, cur) => {
        const { issues: { issues = [] } = {} } = cur
        issues.map((i) => acc.add(i))
        return acc
      }, new Set()))
    }
  },

  computed: {
    /**
     * Tells what date range should be used based on display style
     * @returns {String} What date range to use
     */
    getDataRangeFieldFrmStyle () {
      return resolver.getDateStepSize(this.style)
    },

    getDateRangePresets () {
      let start = moment(this.getEditedCampaignStartDate).add(1, 'day').startOf('day').toDate()
      let end = moment(this.getEditedCampaignEndDate).add(1, 'day').startOf('day').toDate()
      let label = this.$t('DateRange.preset.entireCampaign')

      let rtr = {}

      if (this.isEditingCampaign) {
        rtr.entireCampaign = function () {
          return {
            label: label || 'Celotna kampanja',
            active: false,
            dateRange: {
              start: start,
              end: end
            }
          }
        }
      }

      return Object.assign({}, rtr, drpg.generate(
        ['today', 'thisWeek', 'thisMonth'],
        {
          'today': this.$t('DateRange.preset.today'),
          'thisWeek': this.$t('DateRange.preset.thisWeek'),
          'thisMonth': this.$t('DateRange.preset.thisMonth')
        }))
    },

    /**
     * Gives day of first campaign
     * @returns {String}
     */
    getToday () {
      return this.getViewedDate.id || this.getScheduledFrom
    },

    /**
     * Tells us if curent display style is a calendar view
     * @returns {Boolean} If the curent style is a calendar view
     */
    isCalendarView () {
      return resolver.isCalendarView(this.style)
    },

    /**
     * Scheduled from filter for campaignEditor mixin
     * @returns {DateTimeString} Date from filter
     */
    getScheduledFrom () {
      return this.getViewedDate.from || null
    },

    /**
     * Scheduled untill filter for campaignEditor mixin
     * @returns {DateTimeString} Date to filter
     */
    getScheduledUntil () {
      return this.getViewedDate.to || null
    },

    /**
     * Tells if campaign is beeing edited - other option is advertisable
     * @returns {Boolean}
     */
    isEditingCampaign () {
      return this.$route.name !== 'editQuickPostGroup'
    },

    /**
     * Campaigns filter for campaignEditor mixin
     * @returns {Array} Array of campaign id beeing edited
     */
    getCampaigns () {
      if (this.isEditingCampaign) return [this.editingId]
      return null
    },

    /**
     * Advertisables filter for campaignEditor mixin
     * @returns {Array} Array of campaign id beeing edited
     */
    getAdvertisables () {
      if (!this.isEditingCampaign) return [this.editingId]
      return null
    },

    /**
     * Gives edited mpe's ID
     * @returns {String} Edited MPE's id
     */
    editedMpeId () {
      return this.editing
    },

    /**
     * Gives available actions
     * @returns {Array} Array of available actions
     */
    avalableActions () {
      let rtr = []
        rtr.push({
          label: 'Button.editCampaign',
          name: 'back',
          event: 'prevStep',
          hollow: true,
          iconLeft: 'arrow-left',
          faIcon: true
        })

      if (this.getCampaignsStatus === 'active') {
        rtr.push({
          label: 'Button.stop',
          name: 'stop',
          event: 'stopCampaign',
          style: 'nok-style',
          confirm: true,
          iconRight: 'stop',
          iconColor: '#FE2B41',
          faIcon: true
        })
      } else {
        rtr.push({
          label: 'Button.activate',
          name: 'activate',
          event: 'activate',
          confirm: true,
          style: 'activate-campaign-style',
          iconRight: 'play',
          iconColor: '#FFFFFF',
          faIcon: true
        })
      }
      return rtr
    },

    /**
     * Gives campaign's status
     * @returns {String} Current campaign's status
     */
    getCampaignsStatus () {
      return this.getEditedCampaignData.status
    },

    /**
     * Gives available channel filter options
     * @returns {Array} Array of available channel filter options
     */
    getChannelOptions () {
      let rtr = {}
      for (let mpe of this.getMediaEvents) {
        if (!mpe.special) {
          let id = (mpe.credentials || {}).id
          if (!rtr[id]) {
            rtr[id] = {
              val: id,
              label: (mpe.credentials || {}).label
            }
          }
        }
      }
      return Object.values(rtr)
    },

    /**
     * Gives currently viewed date
     * @returns {DateTimeString} Currently viewed date
     */
    getViewedDate () {
      return this.getFilterFields.date || {}
    },

    /**
     * Gives current filter fields
     * @returns {Object} An object of current filter fields
     */
    getFilterFields () {
      return this.filterFields || {}
    },

    /**
     * Gives current MPE type filter
     * @returns {String} Current filtered MPE type
     */
    getTypeFilter () {
      return this.getFilterFields.type || false
    },

    /**
     * Gives current MPE channel filter
     * @returns {String} Current filtered MPE channel
     */
    getChannelFilter () {
      return this.getFilterFields.channel || false
    },

    /**
     * Gives edited campaign's label
     * @returns {String} Current campaign's label
     */
    campaignLabel () {
      return this.getEditedCampaignData.label || ''
    },

    /**
     * Makes campaign's date optons
     * @returns {Array} Array of available date filters
     */
    campaignDayDates () {
      let {startsAt, expiresAt} = this.getEditedCampaignData
      let format;
      let formatAlt;

      switch (this.style) {
        case 'agendaWeek':
          format = 'DD'
          formatAlt = 'DD MMM. YY'
          break;

        case 'month':
          format = 'MMM YY'
          break;

        default:
          format = 'D. MMM. YY'
      }

      return makeDates.getDateList(startsAt, expiresAt, this.style, format, formatAlt)
    },

    /**
     * Gives current campaign's ID
     * @returns {String} Curent campaign's id
     */
    campaignId () {
      return this.editingId
    },

    /**
     * Gives a list of MPES after the rest of filtering
     * @returns {Array} An array of MPE's to be displayed
     */
    getFilteredMpes () {
      let mpes = this.filterEvents(this.getMediaEvents).sort( function (a, b) {
        let keyA = new Date(a.publishAt || a.startsAt),
            keyB = new Date(b.publishAt || b.startsAt)

        if(keyA < keyB) return -1
        if(keyA > keyB) return 1
        return 0
      })

      mpes = mpes.map((mpe) => {
        mpe = { ...mpe }
        const custom = {}
        // Check issues
        const { valid, issue } = checkContentLength({ ...mpe.payload, type: mpe.type })
        if (!valid) {
          custom.issues = { id: mpe.id, issues: [ issue ] }
        }

        // Check warnings
        if (mpe.payload.statusCut) {
          custom.warnings = { id: mpe.id, warnings: [ CONTENT_CUT ] }
        }
        if (Object.keys(custom).length) {
          mpe.custom = custom
        }
        return mpe
      })
      return mpes
    },

    /**
     * Checks if mpe's have any issues
     * @returns {Array|undefined} Array of issues or undefined if none
     */
    getMpeIssues () {
      // If no issues; return undefined; else return the issues
      let rtr = this.extractMpeIssues(this.getFilteredMpes)
      if (rtr.length) {
        return rtr
      }
    },

    /**
     * Will tell if any filters are set
     * @requires {getFilterFields} An object of filter fields
     * @returns {Boolean} If any filter is active
     */
    areFiltersActive () {
      let fields = this.getFilterFields
      for (let f in fields) {
        // If any fields is not null, than some filter is active
        if (fields[f] !== null) return true
      }

      return false
    },

    /**
     * Gives tooltip labels based on errors
     * @returns {Object}
     */
    getTooltips () {
      return {
        activate: (this.getMpeIssues || []).map((i) => this.$t(i))
      }
    }
  },

  components: {
    'campaign-grid': CampaignGrid,
    'button-nav': ButtonNav,
    'input-select': InputSelect,
    'input-button': InputButton,
    'editor-wrapper': EditorWrapper
  },

  mixins: [
    campaignEditor,
    apiConnector
  ]
}
</script>

<style scoped>
.mpe-filters {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
}

hr {
  padding-bottom: 1px!important;
}
.campaign-title {
  font-size: 16px;
  font-weight: bold;
  line-height: 19px; 
}

.filter-clear-button {
  height: 27px;
  line-height: 23px;
  padding-top: 0;
  padding-bottom: 0;
  font-size: 13px;
  margin: 12px 15px;
}

.bottom-buttons {
  margin-top: 9px;
}

.break-text {
  white-space: -moz-pre-wrap !important;
  white-space: -webkit-pre-wrap;
  white-space: -pre-wrap;
  white-space: -o-pre-wrap;
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-all;
  white-space: normal;
}

.date-range-picker-prefix {
  margin-left: 15px;
  margin-top: 5px;
  margin-bottom: 14px;
  font-size: 12px;
  font-weight: lighter;
}

@media (max-width: 768px) {
  .channel-selection .select {
    width: 100%;
  }
  .list-heading {
    margin-bottom: 11px!important;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/service/campaignManager/CampaignEditor.vue?542fff2e